import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { ContentfulImage, ContentfulLogo } from '@stadionhq-v3/contentful-ui';
import {
  BasicComponentProps,
  getMatchStatus,
  isOptaMatchStatus,
  MATCH_STATUS,
  OptaMatchStatus,
} from '@stadionhq-v3/core';
import { DEFAULT_LOCALE, Locale } from '@core/constants/locales.constants';
import { ArrowRightLight, Live } from '@components/icons';
import { Asset, Broadcaster, Fixture } from '@graphql/__generated__/graphql';
import { Translations } from '@lib/dictionary-keys/__generated__';
import templateConfig from '@templateConfig';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import {
  FixtureCtasArgs,
  MatchdayHeroProps as V3MatchdayHeroProps,
} from '@stadionhq-v3/football-ui';
import { HTMLAttributes } from 'react';
import { MatchDayHeroProps } from '@components/organisms/match-day-hero';
import BroadcastersWrapper from '@components/organisms/broadcasters-wrapper/broadcastersWrapper.component';
import buttonStyles from '@components/v3/atoms/button/button.module.scss';
import classnames from 'classnames';
import { ButtonProps } from '@stadionhq-v3/base-ui';
import { renameKeyFromTitleToText } from '@utils/objects.utils';
import { Route } from '@core/types/routes.types';
import { assetTransform, linkTransform } from '../atoms';
import { FixtureCtaDictionary } from '../layout/getCoreCtaConfig';
import broadcastersWrapperTransform from './broadcastersWrapper.transform';

export type MatchdayHeroProps = BasicComponentProps &
  V3MatchdayHeroProps & {
    nativeProps?: HTMLAttributes<HTMLDivElement>;
    locale: Locale;
    kickOffUtc?: string;
  };

type MatchdayHeroDictionary = FixtureCtaDictionary & {
  live: Translations['fixtures']['status']['live'];
  fullTimeAbbreviated: Translations['fixtures']['status']['fullTimeAbbreviated'];
  days: Translations['fixtures']['countdown']['days'];
  hours: Translations['fixtures']['countdown']['hours'];
  minutes: Translations['fixtures']['countdown']['minutes'];
  seconds: Translations['fixtures']['countdown']['seconds'];
  countdown: Translations['fixtures']['label']['countdown'];
  halfTimeAbbreviated: Translations['fixtures']['status']['halfTimeAbbreviated'];
  penaltiesAbbreviated: Translations['fixtures']['status']['penaltiesAbbreviated'];
  tbcAbbreviated: Translations['fixtures']['status']['tbcAbbreviated'];
  postponedAbbreviated: Translations['fixtures']['status']['postponedAbbreviated'];
  abandonedAbbreviated: Translations['fixtures']['status']['abandonedAbbreviated'];
  cancelledAbbreviated: Translations['fixtures']['status']['cancelledAbbreviated'];
  fullTime: Translations['fixtures']['status']['fullTime'];
};

const matchdayHeroTransform = (
  {
    awayClub,
    homeClub,
    matchData,
    matchPeriod,
    venue,
    additionalLinks,
    kickOffUtc,
    image,
    slug,
    team,
    competition,
    broadcasters,
    dataProviderId,
    matchReport,
  }: CleanContentful<Fixture>,
  dictionary: MatchdayHeroDictionary,
  locale: Locale = DEFAULT_LOCALE,
  fallbackClubCrest?: CleanContentful<Asset>
): MatchDayHeroProps => {
  const isPrematch =
    getMatchStatus(matchPeriod as OptaMatchStatus) === MATCH_STATUS.PRE_MATCH;
  const isLive =
    getMatchStatus(matchPeriod as OptaMatchStatus) === MATCH_STATUS.LIVE;
  const isPostmatch =
    getMatchStatus(matchPeriod as OptaMatchStatus) === MATCH_STATUS.POST_MATCH;

  const createCta = (
    condition: string | undefined,
    route: Route,
    params: Record<string, string>,
    title: string
  ) => {
    if (!condition) return null;

    const url = generateFullPathname({ locale, route, params });
    return renameKeyFromTitleToText(linkTransform({ url, title }, locale));
  };

  const matchCentreCta = createCta(
    slug && matchData?.header ? slug : undefined,
    templateConfig.navigation.routes.MATCH_CENTRE,
    { team: team?.slug || '', fixture: slug || '' },
    dictionary.viewMatchCentre
  );

  const liveVideoStreamCta = undefined;

  const matchReportCta = createCta(
    matchReport?.slug,
    templateConfig.navigation.routes.ARTICLE,
    { article: matchReport?.slug || '' },
    dictionary.viewMatchReport
  );

  const additionalCtas =
    additionalLinks
      ?.map((link) => renameKeyFromTitleToText(linkTransform(link, locale)))
      .filter(Boolean) ?? [];

  const applyButtonStyling = (
    buttons: ButtonProps[],
    maxButtons = 2
  ): ButtonProps[] => {
    const buttonVariants: ButtonProps['variant'][] = ['primary', 'secondary'];

    return buttons
      .filter(Boolean)
      .slice(0, maxButtons)
      .map((button, index) => ({
        ...button,
        variant: buttonVariants[index],
        onDark: true,
        iconAfter: <ArrowRightLight />,
        classNames: [
          classnames([buttonStyles.button, buttonStyles.buttonTokens]),
        ],
        order: index + 1,
      }));
  };

  const getCtaLinks = (): ButtonProps[] => {
    const mappedMatchStatus = getMatchStatus(matchPeriod as OptaMatchStatus);

    if (mappedMatchStatus === MATCH_STATUS.PRE_MATCH) {
      const buttons = [matchCentreCta, liveVideoStreamCta, ...additionalCtas];

      return applyButtonStyling(buttons as ButtonProps[]);
    }

    if (mappedMatchStatus === MATCH_STATUS.LIVE) {
      const buttons = [matchCentreCta, liveVideoStreamCta, ...additionalCtas];

      return applyButtonStyling(buttons as ButtonProps[]);
    }

    if (mappedMatchStatus === MATCH_STATUS.POST_MATCH) {
      const buttons = [matchCentreCta, matchReportCta];

      return applyButtonStyling(buttons as ButtonProps[]);
    }

    return [];
  };

  return {
    locale,
    kickoffTime: kickOffUtc,
    awayClub: {
      clubCrest: awayClub?.clubCrestInverse ? (
        <ContentfulLogo
          {...awayClub?.clubCrestInverse}
          width={92}
          height={92}
        />
      ) : awayClub?.clubCrest ? (
        <ContentfulLogo {...awayClub?.clubCrest} width={92} height={92} />
      ) : fallbackClubCrest ? (
        <ContentfulLogo
          {...assetTransform(fallbackClubCrest)}
          alt="fallback crest"
          width={92}
          height={92}
        />
      ) : undefined,
      clubName: awayClub?.name,
      clubAbbreviation: awayClub?.abbreviation,
      score: matchData?.header?.away?.score,
      aggregateScore: matchData?.header?.away?.aggregateScore,
      penaltyScore: matchData?.header?.away?.penaltyScore,
      halfTimeScore: matchData?.header?.away?.halfTimeScore,
    },
    countdown: isPrematch
      ? {
          startTimeUTC: kickOffUtc,
          divider: '',
          countdown: {
            days: dictionary.days,
            hours: dictionary.hours,
            minutes: dictionary.minutes,
            seconds: dictionary.seconds,
          },
          flexProps: {
            flexDirection: 'column',
            tokens: { gap: '--s-spacer-0' },
          },
        }
      : undefined,
    homeClub: {
      clubCrest: homeClub?.clubCrestInverse ? (
        <ContentfulLogo
          {...homeClub?.clubCrestInverse}
          width={184}
          height={184}
        />
      ) : homeClub?.clubCrest ? (
        <ContentfulLogo {...homeClub?.clubCrest} width={184} height={184} />
      ) : fallbackClubCrest ? (
        <ContentfulLogo
          {...assetTransform(fallbackClubCrest)}
          alt="fallback crest"
          width={184}
          height={184}
        />
      ) : undefined,
      clubName: homeClub?.name,
      clubAbbreviation: homeClub?.abbreviation,
      score: matchData?.header?.home?.score,
      aggregateScore: matchData?.header?.home?.aggregateScore,
      penaltyScore: matchData?.header?.home?.penaltyScore,
      halfTimeScore: matchData?.header?.home?.halfTimeScore,
    },
    liveIndicator: isLive
      ? { title: dictionary.live, icon: <Live /> }
      : isPostmatch
        ? { title: dictionary.fullTimeAbbreviated }
        : undefined,
    ctaLinks: {
      coreCtas: getCtaLinks() as FixtureCtasArgs[],
      additionalCtas: [],
    },
    venue,
    image: image ? <ContentfulImage {...image} /> : undefined,
    ...(matchPeriod && isOptaMatchStatus(matchPeriod) ? { matchPeriod } : {}),
    labels: dictionary,
    competitionName: competition?.name,
    broadcasters: (
      <BroadcastersWrapper
        {...broadcastersWrapperTransform(
          locale,
          dataProviderId,
          broadcasters as Broadcaster[]
        )}
      />
    ),
  };
};

export default matchdayHeroTransform;
